import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { FiMail, FiMessageCircle, FiPhone, FiSend, FiLoader } from 'react-icons/fi'
import { useGlobalChat } from '@/contexts/ChatContext'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'

interface ContactCategory {
    _id: string
    name: string
    color?: string
    icon?: string
}

interface ContactFormData {
    name: string
    email: string
    subject: string
    category: string
    message: string
    priority: 'low' | 'medium' | 'high' | 'urgent'
}

const ContactPage: React.FC = () => {
    const navigate = useNavigate()
    const { openChat } = useGlobalChat()
    const { showSuccess, showError } = useSnackbarContext()

    const [formData, setFormData] = useState<ContactFormData>({
        name: '',
        email: '',
        subject: '',
        category: '',
        message: '',
        priority: 'medium'
    })

    const [categories, setCategories] = useState<ContactCategory[]>([])
    const [isLoadingCategories, setIsLoadingCategories] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)

    // Fetch categories on mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setIsLoadingCategories(true)
                const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/contact/categories`)
                if (!response.ok) throw new Error('Failed to fetch categories')
                const data = await response.json()
                setCategories(data.data || [])
            } catch (error) {
                console.error('Error fetching categories:', error)
                showError('Failed to load categories')
            } finally {
                setIsLoadingCategories(false)
            }
        }

        fetchCategories()
    }, [showError])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.name || !formData.email || !formData.subject || !formData.message || !formData.category) {
            showError('Please fill in all required fields')
            return
        }

        try {
            setIsSubmitting(true)
            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            })

            const data = await response.json()

            if (!response.ok) {
                showError(data.message || 'Failed to submit message')
                return
            }

            showSuccess('Your message has been received. We will get back to you soon!')
            setFormData({
                name: '',
                email: '',
                subject: '',
                category: '',
                message: '',
                priority: 'medium'
            })
        } catch (error) {
            console.error('Error submitting message:', error)
            showError('Failed to submit message')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleLiveChat = () => {
        openChat()
    }

    const handleEmailSupport = () => {
        window.location.href = 'mailto:devsidego@gmail.com?subject=Support%20Request'
    }

    const handlePhoneSupport = () => {
        // Initiate a phone call
        window.location.href = 'tel:+233597537575'
    }

    const handleBrowseFAQ = () => {
        navigate('/faq')
    }

    const handleHelpCenter = () => {
        navigate('/help')
    }

    const contactMethods = [
        {
            icon: <FiMessageCircle className="w-6 h-6" />,
            title: 'Live Chat',
            description: 'Get instant help from our support team',
            details: 'Average response time: 2 minutes',
            availability: 'Available 24/7',
            action: 'Start Chat',
            color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
            onClick: handleLiveChat
        },
        {
            icon: <FiMail className="w-6 h-6" />,
            title: 'Email Support',
            description: 'Send us a detailed message',
            details: 'Perfect for complex issues or feature requests',
            availability: 'Response within 24 hours',
            action: 'Send Email',
            color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
            onClick: handleEmailSupport
        },
        {
            icon: <FiPhone className="w-6 h-6" />,
            title: 'Phone Support',
            description: 'Speak directly with our team',
            details: 'For urgent issues and priority support',
            availability: 'Mon-Fri, 9 AM - 6 PM EST',
            action: 'Call Now',
            color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
            onClick: handlePhoneSupport
        }
    ]

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            {/* Hero Section */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl md:heading-hero text-gray-900 dark:text-white mb-4 sm:mb-6">Contact Us</h1>
                    <p className="text-base sm:text-lg md:text-body-lg text-gray-700 dark:text-gray-300">
                        We're here to help! Get in touch with our support team and we'll respond as quickly as possible.
                    </p>
                </div>
            </section>

            {/* Contact Methods */}
            <section className="py-12 sm:py-16 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 sm:mb-12">
                        <h2 className="text-2xl sm:text-3xl md:heading-section text-gray-900 dark:text-white mb-3 sm:mb-4">Choose Your Preferred Contact Method</h2>
                        <p className="text-base sm:text-lg md:text-body-lg text-gray-700 dark:text-gray-300">
                            Select the option that works best for your needs
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                        {contactMethods.map((method, index) => (
                            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-200 text-center">
                                <div className={`inline-flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full mb-4 sm:mb-6 ${method.color}`}>
                                    {method.icon}
                                </div>
                                <h3 className="text-lg sm:text-xl md:heading-card text-gray-900 dark:text-white mb-2 sm:mb-3">{method.title}</h3>
                                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 mb-2">{method.description}</p>
                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">{method.details}</p>
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 mb-4 sm:mb-6">{method.availability}</p>
                                <button 
                                    onClick={method.onClick}
                                    className="btn-primary w-full"
                                >
                                    {method.action}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Form */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 sm:mb-12">
                        <h2 className="text-2xl sm:text-3xl md:heading-section text-gray-900 dark:text-white mb-3 sm:mb-4">Send Us a Message</h2>
                        <p className="text-base sm:text-lg md:text-body-lg text-gray-700 dark:text-gray-300">
                            Fill out the form below and we'll get back to you within 24 hours
                        </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 md:p-8 shadow-sm">
                        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                <div>
                                    <label htmlFor="name" className="form-label">Full Name *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className="form-input"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className="form-label">Email Address *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className="form-input"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                                <div>
                                    <label htmlFor="category" className="form-label">Category *</label>
                                    {isLoadingCategories ? (
                                        <div className="flex items-center justify-center h-10 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                            <FiLoader className="w-5 h-5 animate-spin text-gray-400" />
                                        </div>
                                    ) : (
                                        <select
                                            id="category"
                                            name="category"
                                            value={formData.category}
                                            onChange={handleInputChange}
                                            required
                                            className="form-select"
                                        >
                                            <option value="">Select a category</option>
                                            {categories.map((category) => (
                                                <option key={category._id} value={category._id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="priority" className="form-label">Priority</label>
                                    <select
                                        id="priority"
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleInputChange}
                                        className="form-select"
                                    >
                                        <option value="low">Low</option>
                                        <option value="medium">Medium</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="subject" className="form-label">Subject *</label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    required
                                    className="form-input"
                                    placeholder="Brief description of your issue"
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="form-label">Message *</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    rows={6}
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    className="form-textarea"
                                    placeholder="Please provide as much detail as possible about your inquiry..."
                                />
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2 sm:pt-4">
                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                    * Required fields
                                </p>
                                <button 
                                    type="submit" 
                                    disabled={isSubmitting}
                                    className="btn-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FiLoader className="w-4 h-4 mr-2 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <FiSend className="w-4 h-4 mr-2" />
                                            Send Message
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </section>

            {/* FAQ Link */}
            <section className="bg-gray-100 dark:bg-gray-800 py-12 sm:py-16 md:py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-2xl sm:text-3xl md:heading-section mb-3 sm:mb-4 text-gray-900 dark:text-white">Before You Contact Us</h2>
                    <p className="text-base sm:text-lg md:text-body-lg text-gray-700 dark:text-gray-300 mb-6 sm:mb-8">
                        Check our comprehensive FAQ section - you might find your answer immediately!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button 
                            onClick={handleBrowseFAQ}
                            className="btn-hero bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 text-white"
                        >
                            Browse FAQ
                        </button>
                        <button 
                            onClick={handleHelpCenter}
                            className="btn-hero bg-transparent border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        >
                            Help Center
                        </button>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default ContactPage
